import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { BookOpen, Bookmark, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DashboardLayout from '@/components/DashboardLayout';
import MathRenderer from '@/components/MathRenderer';
import VoiceAssistant from '@/components/VoiceAssistant';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  { value: 'Telugu', label: 'తెలుగు (Telugu)', icon: '📚' },
  { value: 'Hindi', label: 'हिंदी (Hindi)', icon: '📖' },
  { value: 'English', label: 'English', icon: '📝' },
  { value: 'Mathematics', label: 'Mathematics', icon: '🔢' },
  { value: 'Social', label: 'Social Studies', icon: '🌍' },
  { value: 'Physics', label: 'Physics', icon: '⚛️' },
  { value: 'Biology', label: 'Biology', icon: '🧬' }
];

const MARKS = [1, 2, 4, 8];
const DIFFICULTY_LEVELS = ['Easy', 'Medium', 'Hard'];

export default function AskQuestion() {
  const { getAuthHeaders } = useAuth();
  const [formData, setFormData] = useState({
    question_text: '',
    subject: 'Mathematics',
    marks: 4,
    chapter: '',
    difficulty: 'Medium'
  });
  const [chapters, setChapters] = useState([]);
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchChapters(formData.subject);
  }, [formData.subject]);

  const fetchChapters = async (subject) => {
    try {
      const response = await axios.get(`${API}/subjects/chapters?subject=${subject}`, {
        headers: getAuthHeaders()
      });
      setChapters(response.data.chapters);
      setFormData(prev => ({ ...prev, chapter: '' }));
    } catch (error) {
      console.error('Failed to fetch chapters');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAnswer(null);

    try {
      const payload = {
        ...formData,
        chapter: formData.chapter || null,
        difficulty: formData.difficulty || null
      };

      const response = await axios.post(
        `${API}/questions/ask`,
        payload,
        { headers: getAuthHeaders() }
      );
      setAnswer(response.data);
      toast.success('Answer generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate answer');
    } finally {
      setLoading(false);
    }
  };

  const handleBookmark = async () => {
    if (!answer) return;

    try {
      await axios.patch(
        `${API}/questions/${answer.id}/bookmark`,
        { bookmarked: !answer.bookmarked },
        { headers: getAuthHeaders() }
      );
      setAnswer({ ...answer, bookmarked: !answer.bookmarked });
      toast.success(answer.bookmarked ? 'Removed from bookmarks' : 'Added to bookmarks');
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const handleExportPDF = () => {
    if (!answer) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;

    doc.setFontSize(16);
    doc.text('TopMarks - SSC 10th Board Exam Answer', margin, margin);

    doc.setFontSize(12);
    doc.text(`Subject: ${answer.subject}`, margin, margin + 15);
    doc.text(`Marks: ${answer.marks}`, margin, margin + 25);
    if (answer.chapter) {
      doc.text(`Chapter: ${answer.chapter}`, margin, margin + 35);
    }

    doc.setFontSize(14);
    doc.text('Question:', margin, margin + 50);
    doc.setFontSize(11);
    const questionLines = doc.splitTextToSize(answer.question_text, maxWidth);
    doc.text(questionLines, margin, margin + 60);

    const answerY = margin + 60 + (questionLines.length * 7) + 10;
    doc.setFontSize(14);
    doc.text('Answer:', margin, answerY);
    doc.setFontSize(11);
    const answerLines = doc.splitTextToSize(answer.answer, maxWidth);
    doc.text(answerLines, margin, answerY + 10);

    doc.save(`answer-${answer.subject}-${answer.marks}M.pdf`);
    toast.success('PDF exported successfully!');
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 fade-in">
        {/* Header */}
        <div data-testid="ask-question-header" className="bg-white rounded-lg border border-slate-200 p-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Ask a Question</h1>
          <p className="text-slate-600">Get exam-style answers following SSC board patterns with chapter-wise focus</p>
        </div>

        {/* Question Form */}
        <div data-testid="question-form" className="bg-white rounded-lg border border-slate-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">
                  Subject
                </label>
                <select
                  id="subject"
                  data-testid="subject-select"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0"
                >
                  {SUBJECTS.map((subject) => (
                    <option key={subject.value} value={subject.value}>
                      {subject.icon} {subject.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="chapter" className="block text-sm font-medium text-slate-700 mb-2">
                  Chapter/Topic (Optional)
                </label>
                <select
                  id="chapter"
                  data-testid="chapter-select"
                  value={formData.chapter}
                  onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0"
                >
                  <option value="">All Chapters</option>
                  {chapters.map((chapter, idx) => (
                    <option key={idx} value={chapter}>{chapter}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Marks
                </label>
                <div className="flex space-x-2">
                  {MARKS.map((mark) => (
                    <button
                      key={mark}
                      type="button"
                      data-testid={`marks-btn-${mark}`}
                      onClick={() => setFormData({ ...formData, marks: mark })}
                      className={`flex-1 py-3 rounded-md font-medium transition-colors ${
                        formData.marks === mark
                          ? 'bg-blue-900 text-white'
                          : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {mark}M
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="difficulty" className="block text-sm font-medium text-slate-700 mb-2">
                  <Zap className="w-4 h-4 inline mr-1" />
                  Difficulty Level
                </label>
                <select
                  id="difficulty"
                  data-testid="difficulty-select"
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0"
                >
                  {DIFFICULTY_LEVELS.map((level) => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="question" className="block text-sm font-medium text-slate-700 mb-2">
                Your Question
              </label>
              <textarea
                id="question"
                data-testid="question-textarea"
                value={formData.question_text}
                onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                required
                rows={6}
                className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0 resize-none"
                placeholder="Enter your question here..."
              />
            </div>

            <Button
              data-testid="generate-answer-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6 text-lg btn-primary"
            >
              {loading ? (
                <span className="loading-dots">Generating Answer</span>
              ) : (
                'Generate Answer'
              )}
            </Button>
          </form>
        </div>

        {/* Answer Display */}
        {answer && (
          <div data-testid="answer-display" className="bg-white rounded-lg border border-slate-200 p-8 fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Answer</h2>
              <div className="flex space-x-3">
                <Button
                  data-testid="bookmark-btn"
                  onClick={handleBookmark}
                  variant="outline"
                  className="border-slate-200"
                >
                  <Bookmark className={`w-4 h-4 mr-2 ${answer.bookmarked ? 'fill-current' : ''}`} />
                  {answer.bookmarked ? 'Bookmarked' : 'Bookmark'}
                </Button>
                <Button
                  data-testid="export-pdf-btn"
                  onClick={handleExportPDF}
                  variant="outline"
                  className="border-slate-200"
                >
                  Export PDF
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <span className="marks-badge bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
                  {answer.marks} Marks
                </span>
                <span className="marks-badge bg-slate-100 text-slate-700 px-3 py-1 rounded-full">
                  {answer.subject}
                </span>
                {answer.chapter && (
                  <span className="marks-badge bg-green-50 text-green-700 px-3 py-1 rounded-full">
                    {answer.chapter}
                  </span>
                )}
                {answer.difficulty && (
                  <span className={`marks-badge px-3 py-1 rounded-full ${
                    answer.difficulty === 'Easy' ? 'bg-green-50 text-green-700' :
                    answer.difficulty === 'Medium' ? 'bg-yellow-50 text-yellow-700' :
                    'bg-red-50 text-red-700'
                  }`}>
                    {answer.difficulty}
                  </span>
                )}
              </div>

              {/* Voice Assistant - Only for Telugu and Hindi */}
              {(answer.subject === 'Telugu' || answer.subject === 'Hindi') && (
                <VoiceAssistant 
                  text={answer.answer}
                  language={answer.subject === 'Telugu' ? 'telugu' : 'hindi'}
                />
              )}

              <div className="p-4 bg-slate-50 rounded-md">
                <p className="text-sm font-medium text-slate-700 mb-2">Question:</p>
                <p className="text-slate-900">{answer.question_text}</p>
              </div>

              <div className="p-6 bg-blue-50/30 rounded-md border-l-4 border-blue-900">
                <p className="text-sm font-medium text-slate-700 mb-4">Answer (SSC Exam Style):</p>
                <div className="answer-content text-slate-900">
                  <MathRenderer content={answer.answer} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
